import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import BackToDash from "../components/BackToDash";
import FloatButton from "../components/ui/FloatButton";

const Styles = {
  Headline: styled.h2`
    color: ${({ theme }) => theme.colors.grey4};
    margin: 1rem;
    text-align: center;
  `,
  PageCont: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: 800px;
    height: 100%;
  `,
  Table: styled.table`
    margin: 1rem;
    border: 1px solid black;
    border-collapse: collapse;
    thead th:nth-child(1) {
      width: 30%;
    }
    thead th:nth-child(2) {
      width: 70%;
    }
    th,
    td {
      padding: 1.5rem;
      border: 1px solid black;
    }
  `,
  Para: styled.p`
    margin: 1rem;
  `,
  MarginDiv: styled.div`
    margin: 1rem;
  `,
  ResourceDiv: styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  a {
    // text-decoration: none;
    color: inherit;
    cursor: pointer;
  }

  `,
};

const linkArray = [
  {
    text: "DNREC Data Portal - condition of tidal wetlands",
    link: "https://data.delaware.gov/Energy-and-Environment/Condition-of-Tidal-Wetlands/rgup-nusm",
  },
  { text: "New Jersey TIdal Wetland Monitoring Network", link: "" },
  {
    text: "New Jersey Reference Wetland Tool",
    link: "https://tools.cei.psu.edu/wetlands/njdep/",
  },
  {
    text: "New Jersey Bay Island Restoration Planner",
    link: "https://maps.coastalresilience.org/nj-bay-islands/",
  },
  {
    text: "National Estuarine Research Reserve - System-wide Monitoring Program",
    link: "https://coast.noaa.gov/digitalcoast/data/nerr.html",
  },
  {
    text: "Meadowlands Research and Restoration Institute",
    link: "https://meri.njmeadowlands.gov/",
  },
];

const Instructions = () => {
  let guideLink =
    "https://delawareestuary.s3.amazonaws.com/WATCH+Tool/WATCH+User+Guide+V2+19MAR21.pdf";

  return (
    <Styles.PageCont>
      <Styles.Para>
        The following instructions guide the user through the general steps for
        data input per attribute.
      </Styles.Para>
      <Styles.Para>
        For more detailed guidance,{" "}
        <a href={guideLink}>please see the User's Guide located here.</a>
      </Styles.Para>
      <Styles.Table>
        <caption>For each attribute, data is entered in 5 groups:</caption>
        <thead>
          <tr>
            <th>Group</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Current or Most Recent Metric Value</td>
            <td>
              Current state of the metric at the site. These data are a singular
              value can be collected remotely or in situ.
            </td>
          </tr>
          <tr>
            <td>Reference Criteria</td>
            <td>
              Idealized or required range of values for a metric at the site.
              These data are user selected and can be acquired from
              theoretical/modeled or measured sources.
            </td>
          </tr>
          <tr>
            <td>Trajectory</td>
            <td>
              Trajectories represent the rate of change expected at the site per
              metric. These data are a user selected range from a measured or
              modeled source.
            </td>
          </tr>
          <tr>
            <td>Forecast Metric Range</td>
            <td>Resultant metric values based on given trajectories.</td>
          </tr>
          <tr>
            <td>Concerns</td>
            <td>
              Results of a set of conditional formulas used to integrate metric,
              criteria standard, and trajectory data in order to weigh the
              quantitative evidence regarding site-specific deficiencies.
            </td>
          </tr>
        </tbody>
      </Styles.Table>
      <Styles.MarginDiv>
        <h3>Steps:</h3>
        <ol>
          <li>
            For each attribute, choose one Metric from the drop down menu that
            will be evaluated.
          </li>
          <li>For the Method, select a method from the drop down menu.</li>
          <li>
            For Current or Most Recent Metric Value, enter the current metric
            value and its associated units.
          </li>
          <li>
            Use the Justification to describe how the data were collected or
            source of the data, providing detail as appropriate.
          </li>
          <li>
            Enter the appropriate range of values for the Reference Criteria.
            The units should match the Current Metric Value.
          </li>
          <li>
            Select the appropriate Reference Criteria Type from the drop down
            menu.
          </li>
          <li>
            Use the Criteria Standard Justification to describe why the specific
            values were chosen, how they relate to the Metric and Goal, and from
            where they were sourced.
          </li>
          <li>
            Enter the Metric Trajectory Range values. The Units per Year should
            match the Current Metric Value.
          </li>
          <li>
            Use the Metric Trajectory Justification to describe the source of
            the trajectory data and why it was deemed appropriate.
          </li>
          <li>
            Enter the number of years over which metric change will be evaluated
            in the Forecast section. The Forecasted Metric Range will calculate
            the projected change in the metric from today's value based on the
            Metric Trajectory Range and the Forecast Years.
          </li>
          <li>
            Use the Forecast Justification to describe the reasoning for
            selecting the number of Forecast Years entered.
          </li>
        </ol>
      </Styles.MarginDiv>
      <Styles.MarginDiv>
        <h3>Notes</h3>
        <ul>
          <li>
            All justification tabs must be filled out for the Decision Tool to
            calculate violations.
          </li>
          <li>
            If there is no data for an Attribute, the assumption is that it
            meets chosen Criteria Threshold; absence of data cannot be used to
            identify deficiency.
          </li>
          <li>
            For Horizontal Position data, a negative number indicates erosion
            and a positive number indicates accretion.
          </li>
        </ul>
      </Styles.MarginDiv>
      <Styles.ResourceDiv>
        <h3>Resources</h3>
        <span>
          The following resources can provide reference data for many of the
          attributes and metrics in WATCH:
        </span>
        <ul>
          {linkArray.map(item => (
            <li key={item}>
                <a href={item.link} target='_blank' rel='noreferrer'>
                    {item.text}
                </a>
            </li>
          ))}
        </ul>
      </Styles.ResourceDiv>
      <BackToDash />
    </Styles.PageCont>
  );
};

export default Instructions;
